import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { ChevronRight, X, Play, ArrowRight } from 'lucide-react';

const scenes = [
  {
    image: 'https://images.unsplash.com/photo-1600607686527-6fb886090705?w=1920',
    title: 'Crafting Perfection',
    subtitle: 'Where luxury meets functionality',
    align: 'center'
  },
  {
    image: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=1920',
    title: 'Timeless Design',
    subtitle: 'Every surface tells a story',
    align: 'left'
  },
  {
    image: 'https://images.unsplash.com/photo-1600566752229-250b0ffe885d?w=1920',
    title: 'Your Vision',
    subtitle: 'Brought to life with precision',
    align: 'right'
  }
];

const flickerAnimation = {
  initial: { opacity: 0.4 },
  animate: {
    opacity: [0.4, 1, 0.4, 0.8, 0.4, 1, 0.4],
    transition: {
      duration: 2.5,
      repeat: Infinity,
      repeatType: "reverse" as const,
      ease: "linear",
      times: [0, 0.2, 0.3, 0.5, 0.7, 0.8, 1]
    }
  }
};

export const CinematicIntro: React.FC = () => {
  const { setStep } = useCustomizationStore();
  const [currentScene, setCurrentScene] = useState(0);
  const [isSkipHovered, setIsSkipHovered] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentScene((prev) => {
        const nextScene = (prev + 1) % scenes.length;
        if (nextScene === 0) {
          // When we complete the cycle, move to material selection
          setStep('material-selection');
        }
        return nextScene;
      });
    }, 4000);

    return () => clearInterval(timer);
  }, [setStep]);

  const handleSkip = () => {
    setStep('material-selection');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black z-50"
    >
      {/* Skip Button - Top Right */}
      <button
        onClick={handleSkip}
        className="absolute top-6 right-6 text-white/60 hover:text-white z-20 flex items-center gap-2 px-4 py-2 rounded-lg border border-gold/20 hover:border-gold/40 transition-colors"
      >
        <span>Skip Intro</span>
        <X className="w-5 h-5" />
      </button>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentScene}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="relative w-full h-full"
        >
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${scenes[currentScene].image})` }}
          >
            <div className="absolute inset-0 bg-black/40" />
          </div>

          {/* Content */}
          <div className="relative h-full flex items-center">
            <div
              className={`w-full max-w-7xl mx-auto px-8 ${
                scenes[currentScene].align === 'left'
                  ? 'text-left'
                  : scenes[currentScene].align === 'right'
                  ? 'text-right'
                  : 'text-center'
              }`}
            >
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="text-7xl font-bold text-white mb-6"
              >
                {scenes[currentScene].title}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="text-3xl text-white/90"
              >
                {scenes[currentScene].subtitle}
              </motion.p>
            </div>
          </div>

          {/* Bottom Skip Button with Flicker Effect */}
          <div className="absolute bottom-12 left-0 right-0 flex flex-col items-center gap-4 z-20">
            <motion.button
              initial="initial"
              animate="animate"
              variants={flickerAnimation}
              onHoverStart={() => setIsSkipHovered(true)}
              onHoverEnd={() => setIsSkipHovered(false)}
              onClick={handleSkip}
              className="group"
            >
              <motion.div
                animate={{
                  scale: isSkipHovered ? 1.1 : 1,
                  transition: { duration: 0.2 }
                }}
                className="px-8 py-3 bg-gold/10 backdrop-blur-sm rounded-lg border border-gold/30 group-hover:bg-gold/20 group-hover:border-gold/50 transition-colors flex items-center gap-2"
              >
                <span className="text-white font-medium text-lg">Continue to Experience</span>
                <ArrowRight className="w-5 h-5 text-white" />
              </motion.div>
            </motion.button>

            {/* Progress Indicators */}
            <div className="flex items-center gap-3 mt-4">
              {scenes.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentScene
                      ? 'w-8 bg-gold'
                      : 'bg-gold/50'
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};